import { useMemo } from "react";

import { ScoreBadge } from "@/blocks/ScoreBadge";
import { getColorFromRange } from "@/helpers/range/range.helpers";
import { ColorScale } from "@/helpers/range/range.helpers.decl";

export type CsatRating = "high" | "medium" | "low" | "na";

type CsatScoreProps = {
  // Null is "Not applicable"
  value: number | null;
};

const ratingTone: Record<CsatRating, ScoreBadge.Props["tone"]> = {
  high: "success",
  medium: "warning",
  low: "destructive",
  na: "muted",
};

const csatScale: ColorScale<Exclude<CsatRating, "na">> = {
  high: [71, 100],
  medium: [41, 71],
  low: [0, 41],
};

export function getCsatRating(value: number | null): CsatRating {
  if (value === null) {
    return "na";
  }

  if (value < 0) {
    return "low";
  }

  if (value > 100) {
    return "high";
  }

  return getColorFromRange(csatScale)(value) ?? "na";
}

export type CsatTextColor =
  | "text-success"
  | "text-warning"
  | "text-destructive"
  | "text-muted-foreground";

const ratingTextColor: Record<CsatRating, CsatTextColor> = {
  high: "text-success",
  medium: "text-warning",
  low: "text-destructive",
  na: "text-muted-foreground",
};

export function getCsatRatingTextColor(rating: CsatRating): CsatTextColor {
  return ratingTextColor[rating];
}

export function CsatScore({ value }: CsatScoreProps) {
  const rating = useMemo(() => getCsatRating(value), [value]);

  const computedContent = useMemo(() => {
    if (value === null) {
      return "--";
    }

    if (value < 0) {
      return "0";
    }

    if (value > 100) {
      return "100";
    }

    return `${Math.round(value)}`;
  }, [value]);

  return (
    <ScoreBadge tone={ratingTone[rating]} data-test={`csat-score-${value}`}>
      {computedContent}
    </ScoreBadge>
  );
}
